import type { AnimationEvent, MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { css, keyframes } from 'styled-components';

import { zIndexMap } from '../../../shared/config';
import { useSheetPresence } from '../../../shared/hooks/use-sheet-presence';
import { PrimaryButton, rubikTypographyStyles } from '../../../shared/ui';

const WITHDRAWAL_SHEET_CLOSE_ANIMATION_MS = 240;

type WithdrawalFreeWithdrawalsSheetProps = {
  isOpen: boolean;
  onClose: () => void;
};

const overlayFadeIn = keyframes`
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
`;

const overlayFadeOut = keyframes`
  from {
    opacity: 1;
  }

  to {
    opacity: 0;
  }
`;

const sheetSlideIn = keyframes`
  from {
    transform: translateY(100%);
  }

  to {
    transform: translateY(0);
  }
`;

const sheetSlideOut = keyframes`
  from {
    transform: translateY(0);
  }

  to {
    transform: translateY(100%);
  }
`;

const getOverlayAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${overlayFadeOut} ${WITHDRAWAL_SHEET_CLOSE_ANIMATION_MS}ms
          ease-in both;
      `
    : css`
        animation: ${overlayFadeIn} 300ms ease-out both;
      `;

const getSheetAnimationCss = (isClosing: boolean) =>
  isClosing
    ? css`
        animation: ${sheetSlideOut} ${WITHDRAWAL_SHEET_CLOSE_ANIMATION_MS}ms
          ease-in both;
      `
    : css`
        animation: ${sheetSlideIn} 360ms cubic-bezier(0.22, 0.61, 0.36, 1) both;
      `;

const Overlay = styled.div.attrs({
  'data-ui': 'withdrawal-free-withdrawals-overlay',
})<{ $isClosing: boolean }>`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  padding-top: ${({ theme }) =>
    `max(${theme.scale.px(65)}, env(safe-area-inset-top))`};
  background: ${({ theme }) => theme.colors.surface.backdrop};
  overscroll-behavior: contain;
  touch-action: pan-y;
  z-index: ${zIndexMap.withdrawal.overlay};
  ${({ $isClosing }) => getOverlayAnimationCss($isClosing)};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Sheet = styled.section.attrs({
  'data-ui': 'withdrawal-free-withdrawals-sheet',
})<{ $isClosing: boolean }>`
  display: grid;
  align-content: start;
  gap: ${({ theme }) => theme.scale.px(20)};
  border-top-left-radius: ${({ theme }) => theme.scale.px(21)};
  border-top-right-radius: ${({ theme }) => theme.scale.px(21)};
  padding: ${({ theme }) => theme.scale.px(32)}
    ${({ theme }) => theme.scale.px(24)}
    ${({ theme }) => `max(${theme.scale.px(28)}, env(safe-area-inset-bottom))`};
  background: ${({ theme }) => theme.colors.surface.base};
  z-index: ${zIndexMap.withdrawal.sheet};
  ${({ $isClosing }) => getSheetAnimationCss($isClosing)};

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Title = styled.h2`
  ${rubikTypographyStyles};
  margin: 0;
  padding: 0 ${({ theme }) => theme.scale.px(7)};
  font-size: ${({ theme }) => theme.scale.px(24)};
  font-weight: 600;
  line-height: ${({ theme }) => theme.scale.px(28)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const Intro = styled.p`
  margin: 0;
  padding: 0 ${({ theme }) => theme.scale.px(7)};
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(15)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const SummaryCard = styled.section.attrs({
  'data-ui': 'withdrawal-free-withdrawals-summary-card',
})`
  border-radius: ${({ theme }) => theme.scale.px(10)};
  background: ${({ theme }) => theme.colors.surface.subtle};
  padding: ${({ theme }) => theme.scale.px(26)}
    ${({ theme }) => theme.scale.px(17)};
`;

const SummaryText = styled.p`
  margin: 0;
  white-space: pre-line;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(14)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const DetailsStack = styled.div`
  display: grid;
  gap: ${({ theme }) => theme.scale.px(12)};
  padding: 0 ${({ theme }) => theme.scale.px(7)};
`;

const DetailText = styled.p`
  margin: 0;
  white-space: pre-line;
  font-size: ${({ theme }) => theme.scale.px(12)};
  line-height: ${({ theme }) => theme.scale.px(15)};
  color: ${({ theme }) => theme.colors.text.primary};
`;

const ConfirmButton = styled(PrimaryButton)`
  margin: ${({ theme }) => theme.scale.px(22)} auto 0;
`;

export function WithdrawalFreeWithdrawalsSheet({
  isOpen,
  onClose,
}: WithdrawalFreeWithdrawalsSheetProps) {
  const { t } = useTranslation();
  const { handleCloseAnimationEnd, isClosing, isMounted } =
    useSheetPresence(isOpen);

  const stopOverlayClose = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const handleSheetAnimationEnd = (event: AnimationEvent<HTMLElement>) => {
    if (event.target !== event.currentTarget || !isClosing) {
      return;
    }

    handleCloseAnimationEnd();
  };

  if (!isMounted) {
    return null;
  }

  return (
    <Overlay $isClosing={isClosing} onClick={onClose}>
      <Sheet
        $isClosing={isClosing}
        aria-label={t('withdrawal.freeWithdrawalsSheetTitle')}
        role="dialog"
        onAnimationEnd={handleSheetAnimationEnd}
        onClick={stopOverlayClose}
      >
        <Title>{t('withdrawal.freeWithdrawalsSheetTitle')}</Title>
        <Intro>{t('withdrawal.freeWithdrawalsSheetIntro')}</Intro>
        <SummaryCard>
          <SummaryText>
            {t('withdrawal.freeWithdrawalsSheetSummary')}
          </SummaryText>
        </SummaryCard>
        <DetailsStack>
          <DetailText>
            {t('withdrawal.freeWithdrawalsSheetConditions')}
          </DetailText>
          <DetailText>
            {t('withdrawal.freeWithdrawalsSheetProcessing')}
          </DetailText>
        </DetailsStack>
        <ConfirmButton type="button" onClick={onClose}>
          {t('withdrawal.freeWithdrawalsSheetCta')}
        </ConfirmButton>
      </Sheet>
    </Overlay>
  );
}
